import { useCallback, useEffect, useRef, useState } from 'react';
import useStyle from './index.styles';
import { Tabs, Button, Popover } from 'antd';
import { Helmet } from 'react-helmet-async';
import type { TabsProps } from 'antd';
import { ReactTagManager } from 'react-gtm-ts';
import AddCommentModal from '../../components/AddCommentModal';
import CarouselViewerModal from '../../components/CarouselViewerModal';
import { ReactComponent as Share } from '../../assets/images/share.svg';
import { ReactComponent as Save } from '../../assets/images/save.svg';
import { ReactComponent as Print } from '../../assets/images/print.svg';
import { ReactComponent as Camera } from '../../assets/images/camera.svg';
import image1 from '../../assets/images/memorialImagesBG/image1.svg';
import image2 from '../../assets/images/memorialImagesBG/image2.svg';
import image3 from '../../assets/images/memorialImagesBG/image3.svg';
import image4 from '../../assets/images/memorialImagesBG/image4.svg';
import image5 from '../../assets/images/memorialImagesBG/image5.svg';
import image6 from '../../assets/images/memorialImagesBG/image6.svg';
import image7 from '../../assets/images/memorialImagesBG/image7.svg';
import image8 from '../../assets/images/memorialImagesBG/image8.svg';
import image9 from '../../assets/images/memorialImagesBG/image9.svg';
import image10 from '../../assets/images/memorialImagesBG/image10.svg';
import { notificationMessage, useIsMobile } from '../../utils/hooks';
import useApi from '../../utils/api';
import {
  NavigateFunction,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import Loader from '../../DLS/Loader';
import { useTranslation } from 'react-i18next';
import { downloadBase64, getFormattedDateToShow } from '../../utils';
import { CirclePlaySolid, DocumentInfo } from '../../utils/icons';
import AvatarMemorial from '../../DLS/Avatar';
import CommentCard from '../../components/CommentCard';
import { useMemorial } from '../../context/memorial.provider';
import DLSBreadcrumb from '../../DLS/Breadcrumb';
import LeaveDetail from '../../components/LeaveDetail';
import TimelineInMemorial from '../../components/TimelineInMemorial';
import { TFunction } from 'i18next';

interface TimeLineType {
  description: string;
  date: string;
  imageUrl: string;
  imageName: string;
}
const bgImages = [image1, image2, image3, image4, image5, image6, image7, image8, image9, image10];

const goBack = ({
  searchParams,
  navigate,
}: {
  searchParams: URLSearchParams;
  navigate: NavigateFunction;
}) => {
  const from = searchParams.get('from');

  return from === 'new-memorial'
    ? navigate('/home/memorials/list?page=1')
    : window.history.length <= 2
      ? from === 'my-memorials'
        ? navigate('/home/memorials/list?page=1')
        : navigate('/home/start?page=1&pageSize=9')
      : navigate(-1);
};

const previousPageTitle = ({ searchParams, t }: { searchParams: URLSearchParams; t: TFunction }) =>
  searchParams.get('from') === 'my-memorials'
    ? t('topbar.myMemorials')
    : t('start.exploreMemorials');

const Memorial = () => {
  const classes = useStyle();
  const [bgImage, setBgImage] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTabIndex, setActiveTabIndex] = useState<string>('1');
  const [imageToShow, setImageToShow] = useState<null | {
    files: { url: string; name: string }[];
    index: number;
    isVideo?: boolean;
  }>(null);
  const [memorialInfo, setMemorialInfo] = useState<null | {
    firstName: string;
    lastName: string;
    birthDate: string;
    birthCity: string;
    birthCountry: string;
    dateOfDeath: string;
    fatherName: string;
    motherName: string;
    coupleOf: string;
    childrenNames: string;
    city: string;
    country: string;
    cemeteryInformation: string;
    cemeteryPhoto: string;
    image: string;
    customMessage: string;
    photos: { name: string; url: string }[];
    videos: { name: string; url: string }[];
    timeLine: TimeLineType[];
    QRCode: string;
    id: string;
    comments: {
      firstName: string;
      lastName: string;
      comment: string;
      date: string;
      id: number;
      profileImageUrl: string;
    }[];
    gifExpiredDate: string;
    gifUrl: string;
    creatorId: string;
  }>(null);
  const [facebookPixelScript, setFacebookPixelScript] = useState<string>('');
  const [facebookPixelNoScript, setFacebookPixelNoScript] = useState<string>('');
  const {
    birthCity,
    birthCountry,
    birthDate,
    cemeteryInformation,
    cemeteryPhoto,
    childrenNames,
    city,
    comments,
    country,
    coupleOf,
    customMessage,
    dateOfDeath,
    fatherName,
    firstName,
    gifExpiredDate,
    gifUrl,
    id,
    image,
    lastName,
    motherName,
    photos,
    QRCode,
    timeLine,
    videos,
    creatorId,
  } = memorialInfo || {};

  const isMobile = useIsMobile();
  const URLParams = useParams();
  let [searchParams] = useSearchParams();
  const { api } = useApi();
  const { t } = useTranslation();
  const { id: userId } = useMemorial();
  const navigate = useNavigate();
  const location = useLocation();
  const QRRef = useRef<HTMLDivElement>(null);

  const getMemorialInfo = useCallback(() => {
    setIsLoading(true);
    api()
      .get(`/personal-info?id=${URLParams.id}`)
      .then((response) => {
        if (response.status === 200 && response.data?.data) {
          const {
            personal_information: {
              name,
              last_name,
              full_name_father,
              full_name_mother,
              spouse,
              die_city,
              die_country,
              birth_date,
              die_date,
              location,
              url_photo_location,
              url_photo,
              message,
              id,
              birth_city,
              country,
              owner,
            },
            sons,
            photos,
            videos,
            line_live,
            code_qr,
            book_memories,
            gif,
          } = response.data.data;

          let childrenNames = '';
          sons.forEach((e: any, i: number) => {
            childrenNames += e.name;

            if (i !== sons.length - 1) {
              if (i === sons.length - 2) {
                childrenNames += ` ${t('memorial.and')} `;
              } else {
                childrenNames += ' , ';
              }
            }
          });

          setMemorialInfo({
            firstName: name,
            lastName: last_name,
            birthDate: birth_date,
            birthCity: birth_city,
            birthCountry: country,
            dateOfDeath: die_date,
            fatherName: full_name_father,
            motherName: full_name_mother,
            coupleOf: spouse,
            childrenNames,
            city: die_city,
            country: die_country,
            creatorId: owner,
            cemeteryInformation: location,
            cemeteryPhoto: url_photo_location,
            image: url_photo,
            customMessage: message,
            gifExpiredDate: gif[0]?.expired || '',
            gifUrl: gif[0]?.url || '',
            photos,
            videos,
            timeLine: line_live.map((e: any) => ({
              description: e.description,
              date: e.date,
              imageUrl: e.url,
              imageName: e.name,
            })),
            QRCode: code_qr,
            id,
            comments: book_memories
              .sort((a: any, b: any) => (a.date > b.date ? -1 : 1))
              .map((e: any) => ({
                firstName: e.name,
                lastName: e.last_name,
                comment: e.message,
                date: e.date,
                id: e.id,
                profileImageUrl: e.url_photo || '',
              })),
          });
        }
      })
      .catch(() => navigate('/home/start?page=1&pageSize=9'))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (URLParams.id) {
      getMemorialInfo();
    }
  }, [URLParams.id, getMemorialInfo, isMobile]);

  useEffect(() => {
    if (searchParams.get('payment_success') === 'true') {
      ReactTagManager.action({
        event: 'card',
        page: location.pathname,
      });
      const newUrl = new URLSearchParams(location.search);
      newUrl.delete('payment_success');
      navigate({ pathname: location.pathname, search: newUrl.toString() }, { replace: true });
    }
    if (searchParams.has('newMemorialType')) {
      const newUrl = new URLSearchParams(location.search);
      const newMemorialType = newUrl.get('newMemorialType');
      newUrl.delete('newMemorialType');
      navigate({ pathname: location.pathname, search: newUrl.toString() }, { replace: true });
      setFacebookPixelScript(
        `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '1973169546455603');
        fbq('track', 'PageView');
        fbq('track', 'StartTrial', {value: '0.00', currency: 'EUR', predicted_ltv: ${newMemorialType === 'anual' ? '50.00' : '195.00'}});
      `
      );
      setFacebookPixelNoScript(
        `
        <img height="1" width="1" style="display:none"
        src="https://www.facebook.com/tr?id=1973169546455603&ev=PageView&noscript=1"
        />
      `
      );
    }
  }, [location.pathname, location.search, navigate, searchParams]);

  useEffect(() => {
    const min = 0;
    const max = 9;
    const imageNumber = Math.floor(Math.random() * (max - min + 1) + min);
    setBgImage(bgImages[imageNumber]);
  }, []);

  const renderTabBar: TabsProps['renderTabBar'] = (props, DefaultTabBar) => (
    <DefaultTabBar {...props} style={{ background: '#fff' }} />
  );

  const onCopyUrl = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url.split('?')[0]);
    notificationMessage({
      message: t('memorial.copiedUrl'),
      type: 'success',
    });
  };

  const onPrintQr = () => {
    const content = QRRef.current?.outerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');

    if (content && printWindow) {
      printWindow.document.write('<html><head><title>Imprimir Imagen</title>');
      printWindow.document.write('</head><body>');
      printWindow.document.write(content);
      printWindow.document.write('</body></html>');
      printWindow.document.close();

      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      };
    }
  };

  const getTabsContent = () => {
    return [
      {
        label: isMobile ? (
          <Button className={activeTabIndex === '1' ? classes.selectedTabInMobile : ''}>
            {t('memorial.memoriesBook')}
          </Button>
        ) : (
          t('memorial.memoriesBook')
        ),
        key: '1',
        children: (
          <>
            <div className="relative">
              <div className="md:absolute md:right-[0px] md:top-[-16px]">
                <AddCommentModal
                  isMemorialCreator={userId === creatorId}
                  memorialId={URLParams.id || ''}
                  reloadMemorial={() => getMemorialInfo()}
                  deceasedName={`${firstName} ${lastName}`}
                />
              </div>
            </div>
            <>
              {comments?.length === 0 && (
                <div className="flex min-h-[440px] flex-col items-center justify-center">
                  <div className="flex max-w-[388px] flex-col items-center">
                    <h3 className="m-[0px] text-center">{t('memorial.withoutComments.title')}</h3>
                    <p className="py-[24px] text-center">{t('memorial.withoutComments.message')}</p>
                    <Button onClick={() => setActiveTabIndex('5')}>
                      {t('memorial.withoutComments.button')}
                    </Button>
                  </div>
                </div>
              )}
            </>
            {comments?.length !== 0 && (
              <div className="md:pt-[48px]">
                {comments?.map((e) => (
                  <CommentCard
                    firstName={e.firstName}
                    lastName={e.lastName}
                    copy={e.comment}
                    date={e.date}
                    profileImageUrl={e.profileImageUrl}
                    key={`comment-${e.id}`}
                  />
                ))}
              </div>
            )}
          </>
        ),
      },
      ...(creatorId === userId || photos?.length
        ? [
            {
              label: isMobile ? (
                <Button className={activeTabIndex === '2' ? classes.selectedTabInMobile : ''}>
                  {t('memorial.photos')}
                </Button>
              ) : (
                t('memorial.photos')
              ),
              key: '2',
              children: (
                <>
                  {photos && photos.length !== 0 && (
                    <div className="grid grid-cols-1 gap-[16px] md:grid-cols-3 md:px-[8px] md:pb-[8px]">
                      {photos.map((e, i) => (
                        <img
                          key={`image-${i}`}
                          src={e.url}
                          alt=""
                          className="max-h-[400px] min-h-[400px] w-full cursor-pointer rounded-[8px] object-cover md:max-h-[284px] md:min-h-[284px]"
                          onClick={() => {
                            setImageToShow({
                              files: photos,
                              index: i,
                            });
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {photos?.length === 0 && (
                    <div className="flex min-h-[440px] flex-col items-center justify-center">
                      <div className="flex max-w-[420px] flex-col items-center">
                        <h3 className="m-[0px] text-center">{t('memorial.withoutPhotos.title')}</h3>
                        <p className="py-[24px] text-center">
                          {t('memorial.withoutPhotos.message')}
                        </p>
                        <Button
                          type="primary"
                          onClick={() => navigate(`/home/memorials/new/${id}`)}
                        >
                          {t('memorial.withoutPhotos.button')}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ),
            },
          ]
        : []),
      ...(creatorId === userId || videos?.length
        ? [
            {
              label: isMobile ? (
                <Button className={activeTabIndex === '3' ? classes.selectedTabInMobile : ''}>
                  {t('memorial.videos')}
                </Button>
              ) : (
                t('memorial.videos')
              ),
              key: '3',
              children: (
                <>
                  {videos && videos.length !== 0 && (
                    <div className="grid grid-cols-1 gap-[16px] md:grid-cols-3 md:px-[8px] md:pb-[8px]">
                      {videos.map((e, i) => (
                        <div
                          className="relative max-h-[400px] min-h-[400px] w-full md:max-h-[284px] md:min-h-[284px] [&>svg>path]:fill-bg4 [&>svg]:h-[58px] [&>svg]:w-[58px]"
                          key={`video-${i}`}
                          onClick={() => {
                            setImageToShow({
                              files: videos,
                              index: i,
                              isVideo: true,
                            });
                          }}
                        >
                          <CirclePlaySolid className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform cursor-pointer" />
                          <video
                            src={e.url}
                            className="max-h-[400px] min-h-[400px] w-full cursor-pointer rounded-[8px] object-cover md:max-h-[284px] md:min-h-[284px]"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {videos?.length === 0 && (
                    <div className="flex min-h-[440px] flex-col items-center justify-center">
                      <div className="flex max-w-[420px] flex-col items-center">
                        <h3 className="m-[0px] text-center">{t('memorial.withoutVideos.title')}</h3>
                        <p className="py-[24px] text-center">
                          {t('memorial.withoutVideos.message')}
                        </p>
                        <Button
                          type="primary"
                          onClick={() => navigate(`/home/memorials/new/${id}`)}
                        >
                          {t('memorial.withoutVideos.button')}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ),
            },
          ]
        : []),
      ...(creatorId === userId || timeLine?.length
        ? [
            {
              label: isMobile ? (
                <Button className={activeTabIndex === '4' ? classes.selectedTabInMobile : ''}>
                  {t('memorial.timeLine')}
                </Button>
              ) : (
                t('memorial.timeLine')
              ),
              key: '4',
              children: (
                <>
                  {timeLine?.length === 0 && (
                    <div className="flex min-h-[440px] flex-col items-center justify-center">
                      <div className="flex max-w-[420px] flex-col items-center">
                        <h3 className="m-[0px] text-center">
                          {t('memorial.withoutTimeLine.title')}
                        </h3>
                        <p className="py-[24px] text-center">
                          {t('memorial.withoutTimeLine.message')}
                        </p>
                        <Button
                          type="primary"
                          onClick={() => navigate(`/home/memorials/new/${id}`)}
                        >
                          {t('memorial.withoutTimeLine.button')}
                        </Button>
                      </div>
                    </div>
                  )}
                  {timeLine?.length !== 0 && (
                    <TimelineInMemorial
                      timeLineArray={timeLine as TimeLineType[]}
                      onClickInImage={(name, url) =>
                        setImageToShow({ files: [{ url, name }], index: 0 })
                      }
                    />
                  )}
                </>
              ),
            },
          ]
        : []),
      {
        label: isMobile ? (
          <Button className={activeTabIndex === '5' ? classes.selectedTabInMobile : ''}>
            {t('memorial.QRCode')}
          </Button>
        ) : (
          t('memorial.QRCode')
        ),
        key: '5',
        children: (
          <div className="flex flex-col justify-center p-[16px] md:flex-row md:p-[24px]">
            <div className="mb-[24px] max-w-[330px] md:mb-0">
              <p className="mb-[24px]">{t('memorial.QRCodeMessage1')}</p>
              <p>{t('memorial.QRCodeMessage2')}</p>
            </div>
            <div className="flex flex-col items-center md:ml-[24px]">
              <div ref={QRRef}>
                <img
                  className="max-h-[230px] min-h-[230px] min-w-[230px] max-w-[230px]"
                  src={QRCode}
                  alt=""
                />
              </div>

              <div className="mt-[16px] flex items-center justify-center">
                <Share className="mr-8 cursor-pointer" onClick={onCopyUrl} />
                <Save
                  className="mr-8 cursor-pointer"
                  onClick={() => QRCode && downloadBase64(QRCode, 'QR code')}
                />
                <Print className="cursor-pointer" onClick={onPrintQr} />
              </div>
            </div>
          </div>
        ),
      },
      {
        label: (
          <Popover
            content={
              isMobile ? (
                <div className="max-w-[160px]">
                  <p className="paragraphBold">{t('memorial.wrongContent.title')}</p>
                  <p className="my-[8px]">{t('memorial.wrongContent.text')}</p>
                  <Button onClick={() => navigate('/home/contact-us')}>
                    {t('memorial.wrongContent.buttonText')}
                  </Button>
                </div>
              ) : (
                t('memorial.wrongContent.title')
              )
            }
            trigger={isMobile ? 'click' : 'hover'}
            placement={isMobile ? 'left' : 'top'}
            rootClassName={isMobile ? 'whitePopover' : ''}
          >
            <Button
              className="small icon min-w-[32px] border-0 p-0"
              onClick={isMobile ? undefined : () => navigate('/home/contact-us')}
            >
              <DocumentInfo className="[&>path]:fill-text3" />
            </Button>
          </Popover>
        ),
        key: '6',
        children: <></>,
        disabled: true,
      },
    ];
  };

  return (
    <div className="flex min-h-[calc(100vh-80px)] flex-col px-[16px]">
      {memorialInfo && (
        <Helmet>
          <title>{`${firstName} ${lastName}`}</title>
          <meta
            name="description"
            content={`${firstName} ${lastName}${
              cemeteryInformation ? `, ${cemeteryInformation}` : ''
            }, ${customMessage}`}
          />
          {facebookPixelScript && <script>{facebookPixelScript}</script>}
          {facebookPixelNoScript && <noscript>{facebookPixelNoScript}</noscript>}
        </Helmet>
      )}
      {isLoading && (
        <div className="flex flex-1 items-center justify-center">
          <Loader />
        </div>
      )}
      {!isLoading && memorialInfo && (
        <>
          <CarouselViewerModal
            open={!!imageToShow}
            close={() => setImageToShow(null)}
            files={imageToShow?.files || []}
            index={imageToShow?.index ?? null}
            isVideo={imageToShow?.isVideo ?? false}
          />
          {searchParams.has('from') && (
            <div className="ml-[16px] mt-[24px] md:ml-0 md:mt-[32px]">
              <DLSBreadcrumb
                goBack={() => goBack({ searchParams, navigate })}
                items={[
                  {
                    title: previousPageTitle({ searchParams, t }),
                    onClick: () => goBack({ searchParams, navigate }),
                  },
                  { title: `${firstName} ${lastName}` },
                ]}
              />
            </div>
          )}
          <div
            className={`flex flex-col md:flex-row md:justify-between ${
              searchParams.has('from') ? 'mt-[16px]' : 'mt-[40px]'
            } `}
          >
            <div
              className="mb-[40px] flex flex-1 flex-col items-center md:mb-0 md:flex-row md:justify-between md:rounded-[8px] md:bg-cover md:px-[56px] md:shadow-100"
              style={isMobile ? {} : { backgroundImage: `url(${bgImage})` }}
            >
              <AvatarMemorial image={image || ''} initials="" size={208} shape="square" />
              <div className="flex w-[280px] flex-1 flex-col justify-between md:ml-[24px] md:w-[336px]">
                <div>
                  <h2 className="mb-0 mt-[16px] text-center md:mt-0 md:text-left">{`${firstName} ${lastName}`}</h2>
                  <ul className="m-[0px] mt-[24px] p-[0px] md:mt-[16px] md:pl-[24px]">
                    <li>
                      <p>
                        {t('memorial.bornIn', {
                          birthCity: birthCity || birthCountry,
                          birthDate: getFormattedDateToShow(birthDate),
                        })}
                      </p>
                    </li>
                    {(fatherName || motherName) && (
                      <li>
                        <p>
                          {`${t('memorial.sonOf')} ${fatherName}${
                            fatherName && motherName ? ` ${t('memorial.and')} ` : ''
                          }${motherName}`}
                        </p>
                      </li>
                    )}
                    {coupleOf && (
                      <li>
                        <p>{t('memorial.coupleOf', { coupleOf })}</p>
                      </li>
                    )}
                    {childrenNames && (
                      <li>
                        <p>{t('memorial.sons', { childrenNames })}</p>
                      </li>
                    )}
                    <li>
                      <p>
                        {t('memorial.dieIn', {
                          city: city || country,
                          dateOfDeath: getFormattedDateToShow(dateOfDeath),
                        })}
                      </p>
                    </li>
                  </ul>
                </div>
                {(cemeteryInformation || cemeteryPhoto) && (
                  <div className="relative left-[-24px] mt-[24px] flex md:left-0 md:mt-0 [&>svg]:min-w-[40px]">
                    {cemeteryPhoto && (
                      <Camera
                        onClick={() =>
                          setImageToShow({
                            files: [{ url: cemeteryPhoto, name: t('memorial.restingPlace') }],
                            index: 0,
                          })
                        }
                        className="relative top-[-4px] mr-[4px] cursor-pointer"
                      />
                    )}
                    <div className="md:w-[300px]">
                      {cemeteryInformation && (
                        <p>{t('memorial.restsOn', { cemeteryInformation })}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <LeaveDetail
              deceasedName={`${firstName} ${lastName}`}
              gifExpiredDate={gifExpiredDate || ''}
              reloadMemorial={() => getMemorialInfo()}
              selectedDetail={gifUrl || ''}
            />
          </div>

          <div className="mx-[16px] mb-[48px] mt-[24px] rounded-[8px] border border-solid border-bg1 bg-bg4 p-[24px] md:mx-0 md:mb-[24px] md:mt-[16px]">
            <p className="text-left">{customMessage}</p>
          </div>
          <div
            className={`${classes.tabsContainer} mx-[16px] mb-[40px] rounded-[8px] border border-solid border-bg1 bg-bg4 p-[16px] shadow-100 md:mx-0`}
          >
            <Tabs
              renderTabBar={renderTabBar}
              items={getTabsContent()}
              onTabClick={(index) => setActiveTabIndex(index)}
              activeKey={activeTabIndex}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Memorial;
